/**
 * components/wizard/FreeDrawTools.jsx — 🆕 Phase 4 · Freies Zeichnen.
 *
 * Präsentations-Bausteine für den Free-Draw-Modus im Anker-Sammelmodus:
 *   · LINE_CLASS_META — 6 v8f-LINE_CLASSES + aux-Klasse line_midfield
 *   · LineClassPitch — Klassen-Palette, räumlich wie das Feld angeordnet
 *   · FreeDrawOverlay — SVG: Segmente, Geister (gedimmt/gestrichelt),
 *     Endpunkt-Griffe zum Nachrücken, Drag-Preview
 * Zeichen-/Hotkey-Logik lebt in AnchorCollectMode.jsx.
 */
import React from "react";

const BASE_META = {
  back_line_left: { label: "Backline LINKS", short: "BL·L", color: "#f43f5e", hotkey: "B" },
  goal_line_left: { label: "Goallinie LINKS", short: "GL·L", color: "#f59e0b", hotkey: "G" },
  goal_line_right: { label: "Goallinie RECHTS", short: "GL·R", color: "#22d3ee", hotkey: "G" },
  back_line_right: { label: "Backline RECHTS", short: "BL·R", color: "#a3e635", hotkey: "B" },
  sideline_bottom: { label: "Seitenlinie UNTEN", short: "SL·U", color: "#e879f9", hotkey: "S" },
  sideline_top: { label: "Seitenlinie OBEN", short: "SL·O", color: "#38bdf8", hotkey: "S" },
};

// Aux-Klasse (Fremd-Mittellinie): reine Solver-Constraint, KEIN
// Trainings-Kanal — nur verfügbar wenn game_config.aux_midline_visible.
const AUX_META = {
  line_midfield: { label: "Mittellinie (Fremd)", short: "ML", color: "#fb923c", hotkey: "M" },
};

export const LINE_CLASS_META = { ...BASE_META, ...AUX_META };
export const FREE_LINE_CLASSES = Object.keys(BASE_META);
export const FREE_AUX_CLASSES = Object.keys(AUX_META);
export const VERTICAL_CLASSES = [
  "back_line_left", "goal_line_left", "goal_line_right", "back_line_right",
  "line_midfield",
];

// Hotkey-Toggle (User-Entscheid 1a): wiederholtes Drücken wechselt die Seite.
export const HOTKEY_CYCLE = {
  s: ["sideline_bottom", "sideline_top"],
  g: ["goal_line_left", "goal_line_right"],
  b: ["back_line_left", "back_line_right"],
  m: ["line_midfield", "line_midfield"],
};

/**
 * 🆕 2026-08-23 · LineClassPitch — dieselben Klassen, aber räumlich
 * angeordnet: die Position des Knopfes ist die Position der Linie im
 * Bild.
 *
 *              [ Seitenlinie OBEN ]
 *   [BL·L] [GL·L] [Mitte] [GL·R] [BL·R]
 *              [ Seitenlinie UNTEN ]
 *
 * Vorher lagen die sechs Klassen in einem 2-spaltigen Raster in
 * Objekt-Reihenfolge — links standen BL·L, GL·R, SL·U, rechts GL·L,
 * BL·R, SL·O. Das ist keine Ordnung, die man sich merken kann, und beim
 * Zeichnen sucht man den Knopf jedes Mal neu. Mit der Feld-Anordnung
 * zeigt der Knopf dorthin, wo die Linie im Video liegt.
 *
 * Die Kurzcodes (BL·L, GL·R …) genügen, weil die Anordnung die Bedeutung
 * trägt; der volle Name steht im Tooltip. Hotkeys bleiben wie sie sind
 * (S/G/B/M, zweites Drücken wechselt die Seite) und stehen auf dem Knopf.
 */
const MITTE = "line_midfield";

const PitchButton = ({ cid, active, drawn, onSelect, full = false }) => {
  const m = LINE_CLASS_META[cid];
  const isActive = active === cid;
  return (
    <button
      type="button"
      data-testid={`free-draw-chip-${cid}`}
      onClick={() => onSelect(cid)}
      className={`flex items-center justify-center gap-1 border rounded-sm px-1.5 py-1 font-mono text-[10px] uppercase tracking-wider transition-colors ${
        full ? "w-full" : ""
      } ${isActive ? "ring-2" : "opacity-80 hover:opacity-100"}`}
      style={{
        borderColor: m.color,
        backgroundColor: isActive ? `${m.color}2e` : `${m.color}10`,
        color: m.color,
        "--tw-ring-color": m.color,
      }}
      title={`${m.label} · Hotkey ${m.hotkey} — erneut drücken wechselt die Seite${drawn ? " · Segment gezeichnet" : ""}`}
    >
      <span className="inline-block w-3 h-1 rounded-sm shrink-0" style={{ backgroundColor: m.color }} />
      <span className="truncate">{m.short}</span>
      {drawn && <span aria-label="gezeichnet">✓</span>}
      <kbd className="border rounded-sm px-1 opacity-70 shrink-0" style={{ borderColor: m.color }}>
        {m.hotkey}
      </kbd>
    </button>
  );
};

export const LineClassPitch = ({
  active, onSelect, drawnClasses = [], classes = FREE_LINE_CLASSES,
}) => {
  const hat = (c) => classes.includes(c);
  const btn = (c, full = false) => (
    <PitchButton
      cid={c}
      active={active}
      drawn={drawnClasses.includes(c)}
      onSelect={onSelect}
      full={full}
    />
  );
  // Waagerechte in Bild-Reihenfolge, Senkrechte von links nach rechts.
  const senkrecht = ["back_line_left", "goal_line_left", MITTE,
    "goal_line_right", "back_line_right"].filter(hat);
  return (
    <div data-testid="free-draw-chips" className="space-y-1">
      {hat("sideline_top") && btn("sideline_top", true)}
      <div
        className="grid gap-1"
        style={{ gridTemplateColumns: `repeat(${senkrecht.length}, minmax(0, 1fr))` }}
      >
        {senkrecht.map((c) => (
          <React.Fragment key={c}>{btn(c)}</React.Fragment>
        ))}
      </div>
      {hat("sideline_bottom") && btn("sideline_bottom", true)}
    </div>
  );
};

export const FreeDrawOverlay = ({ segments, drag, imgDims, activeCls, zoom = 1 }) => {
  if (imgDims.w <= 1) return null;
  // 🆕 2026-08-23 · Gegen-Skalierung: alles hier ist in BILDpixeln
  // gerechnet, der Zoom-Transform skaliert es also mit. Eine Linie, die bei
  // 8-fachem Zoom 8-mal dicker wird, verdeckt genau die Feldkante, die man
  // nachziehen will. Durch den Zoom geteilt bleibt die Strichstaerke auf
  // dem SCHIRM konstant.
  const sw = Math.max(2.5, imgDims.w / 420) / zoom;
  return (
    <svg
      data-testid="free-draw-overlay"
      className="absolute inset-0 w-full h-full pointer-events-none"
      viewBox={`0 0 ${imgDims.w} ${imgDims.h}`}
      preserveAspectRatio="none"
    >
      {segments.map((s, i) => {
        const m = LINE_CLASS_META[s.cls];
        const ghost = !!s.ghost;
        // 🆕 2026-08-25 · Zwei Sorten Geister, die man auseinanderhalten muss:
        //   ⟲ MITGEZOGEN — deine eigene Linie, über die Kamerapose in diesen
        //     Frame gerechnet. Lang gestrichelt, gefüllte Griffe.
        //   ? VORSCHLAG  — vom Modell geraten, unbestätigt. Punktiert, hohle
        //     Griffe.
        // Vorher sahen beide gleich aus, und im Bild war nicht erkennbar, ob
        // eine Linie von einem selbst oder vom Modell stammt (Rückmeldung des
        // Nutzers, 25.08.). Die KLASSENFARBE bleibt in beiden Fällen gleich —
        // sie sagt, welche Linie gemeint ist, nicht woher sie kommt.
        const vorschlag = ghost && !!s.vorschlag;
        const strich = vorschlag ? "1 10" : ghost ? "12 8" : undefined;
        const r = ghost ? sw * 2.4 : sw * 1.6;
        return (
          <g key={`${s.cls}-${i}`} opacity={ghost ? 0.45 : 1}>
            <line
              x1={s.x1} y1={s.y1} x2={s.x2} y2={s.y2}
              stroke={m.color} strokeWidth={sw} strokeLinecap="round"
              strokeDasharray={strich}
            />
            {/* Endpunkt-Griffe (Nachrücken): Geister größer, Vorschläge hohl */}
            <circle cx={s.x1} cy={s.y1} r={r}
                    fill={vorschlag ? "none" : m.color}
                    fillOpacity={ghost ? 0.7 : 1}
                    stroke={vorschlag ? m.color : "#000"}
                    strokeWidth={vorschlag ? sw * 0.7 : 0.5} />
            <circle cx={s.x2} cy={s.y2} r={r}
                    fill={vorschlag ? "none" : m.color}
                    fillOpacity={ghost ? 0.7 : 1}
                    stroke={vorschlag ? m.color : "#000"}
                    strokeWidth={vorschlag ? sw * 0.7 : 0.5} />
            <text
              x={(s.x1 + s.x2) / 2} y={(s.y1 + s.y2) / 2 - sw * 3}
              fill={m.color} fontSize={Math.max(12, imgDims.w / 90) / zoom}
              fontFamily="monospace" textAnchor="middle"
              stroke="#000" strokeWidth={0.6} paintOrder="stroke"
            >
              {m.short}{vorschlag ? " ?" : ghost ? " ⟲" : ""}
            </text>
          </g>
        );
      })}
      {drag && (
        <line
          x1={drag.x1} y1={drag.y1} x2={drag.x2} y2={drag.y2}
          stroke={LINE_CLASS_META[activeCls].color}
          strokeWidth={sw} strokeDasharray="10 7" strokeLinecap="round"
          opacity={0.85}
        />
      )}
    </svg>
  );
};
